import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { getAnalyticsInstance } from "../firebaseConfig";
import { capturePostHogEvent, registerPostHogProperties } from "../posthog";

type EventParams = Record<string, string | number | boolean>;

interface UserProperties {
  company_id?: string;
  company_name?: string;
  user_role?: string;
  subscription_plan?: string;
  [key: string]: string | undefined;
}

/**
 * Track a custom event.
 *
 * Escribe a los dos destinos: PostHog (comportamiento por usuario) y Firebase
 * Analytics (agregados). Cada proveedor tiene su propio aislamiento de errores,
 * asi que un fallo de uno nunca suprime al otro.
 *
 * PostHog va primero y de forma sincrona a proposito: la rama de Firebase hace
 * `await isSupported()`, que sondea IndexedDB y puede colgarse indefinidamente
 * en navegadores endurecidos. Si PostHog fuera despues, heredaria ese cuelgue.
 *
 * Sigue siendo `async` para no romper a ningun llamador existente; la parte de
 * PostHog es fire-and-forget sincrona dentro de la promesa.
 */
export const trackEvent = async (
  eventName: string,
  params?: EventParams
): Promise<void> => {
  capturePostHogEvent(eventName, params);

  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      logEvent(analytics, eventName, params);
    }
  } catch (error) {
    console.error("[Analytics] Error tracking event:", error);
  }
};

/**
 * Track page view
 */
export const trackPageView = async (
  pageName: string,
  pageLocation?: string
): Promise<void> => {
  capturePostHogEvent("$pageview", {
    page_name: pageName,
    $current_url: pageLocation || window.location.href,
  });

  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      logEvent(analytics, "page_view", {
        page_title: pageName,
        page_location: pageLocation || window.location.href,
        page_path: window.location.pathname,
      });
    }
  } catch (error) {
    console.error("[Analytics] Error tracking page view:", error);
  }
};

/**
 * Set the current user ID for analytics
 */
export const setAnalyticsUserId = async (userId: string): Promise<void> => {
  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      setUserId(analytics, userId);
    }
  } catch (error) {
    console.error("[Analytics] Error setting user ID:", error);
  }
};

/**
 * Set user properties for segmentation
 */
export const setAnalyticsUserProperties = async (
  properties: UserProperties
): Promise<void> => {
  // Super propiedades, no propiedades de persona: con
  // `person_profiles: "identified_only"` llamar a setPersonProperties sobre un
  // visitante anonimo lo promoveria a persona identificada y anularia el
  // ajuste. Las propiedades de persona las fija identifyPostHog y nadie mas.
  registerPostHogProperties(properties);

  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      setUserProperties(analytics, properties);
    }
  } catch (error) {
    console.error("[Analytics] Error setting user properties:", error);
  }
};

/**
 * Borra la identidad en Firebase Analytics al cerrar sesión.
 *
 * GA4 no tiene un "unset" de propiedades: se sobrescriben con null, que es la
 * forma documentada de limpiarlas.
 */
export const clearAnalyticsUser = async (): Promise<void> => {
  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      setUserId(analytics, null);
      setUserProperties(analytics, {
        company_id: null,
        company_name: null,
        user_role: null,
      });
    }
  } catch (error) {
    console.error("[Analytics] Error clearing user:", error);
  }
};

/**
 * Track user login
 */
export const trackLogin = async (method: string): Promise<void> => {
  await trackEvent("login", { method });
};

/**
 * Track user logout
 */
export const trackLogout = async (): Promise<void> => {
  await trackEvent("logout");
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = async (
  featureName: string,
  action: string,
  additionalParams?: EventParams
): Promise<void> => {
  await trackEvent("feature_usage", {
    feature_name: featureName,
    action,
    ...additionalParams,
  });
};

/**
 * Track navigation between apps
 */
export const trackAppNavigation = async (
  fromApp: string,
  toApp: string
): Promise<void> => {
  await trackEvent("app_navigation", {
    from_app: fromApp,
    to_app: toApp,
  });
};

/**
 * Track errors
 */
export const trackError = async (
  errorType: string,
  errorMessage: string,
  errorLocation?: string
): Promise<void> => {
  await trackEvent("app_error", {
    error_type: errorType,
    error_message: errorMessage.slice(0, 100),
    error_location: errorLocation || window.location.pathname,
  });
};

/**
 * Track search queries
 */
export const trackSearch = async (
  searchTerm: string,
  searchLocation: string,
  resultsCount?: number
): Promise<void> => {
  await trackEvent("search", {
    search_term: searchTerm,
    search_location: searchLocation,
    results_count: resultsCount ?? 0,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = async (
  buttonName: string,
  buttonLocation: string
): Promise<void> => {
  await trackEvent("button_click", {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = async (
  formName: string,
  success: boolean
): Promise<void> => {
  await trackEvent("form_submit", {
    form_name: formName,
    success,
  });
};

/**
 * Track vehicle/device actions
 */
export const trackVehicleAction = async (
  action: string,
  vehicleId: string,
  additionalParams?: EventParams
): Promise<void> => {
  await trackEvent("vehicle_action", {
    action,
    vehicle_id: vehicleId,
    ...additionalParams,
  });
};

/**
 * Track report generation
 */
export const trackReportGeneration = async (
  reportType: string,
  dateRange?: string
): Promise<void> => {
  await trackEvent("report_generated", {
    report_type: reportType,
    date_range: dateRange || "custom",
  });
};

/**
 * Track dashboard interactions
 */
export const trackDashboardInteraction = async (
  dashboardName: string,
  interactionType: string,
  widgetName?: string
): Promise<void> => {
  await trackEvent("dashboard_interaction", {
    dashboard_name: dashboardName,
    interaction_type: interactionType,
    widget_name: widgetName || "",
  });
};
